// R1b-γ — Operations Projects tab. Tenant-scoped projects list.
// B2a: `?status=on_hold` (from the Command Center "Jobs On Hold" card) filters projects and also lists on-hold contracts.
import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../../../lib/api";
import EmptyState from "../../../components/EmptyState";
import { HardHat } from "lucide-react";
import StatusChip from "../../../components/StatusChip";

function OnHoldContracts() {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get("/contracts", { params: { status: "on_hold" } }).then((r) => setRows(r.data || [])).catch(() => setRows([]));
  }, []);
  return (
    <section className="mt-6" data-testid="on-hold-contracts">
      <h2 className="font-display font-semibold text-erf-dark">Contracts On Hold</h2>
      <p className="text-xs font-nav text-erf-dark/60 mt-0.5" data-testid="on-hold-contracts-count">
        {rows === null ? "Loading…" : `${rows.length} contract(s) with status On Hold`}
      </p>
      {rows && rows.length > 0 && (
        <ul className="mt-2 divide-y divide-erf-borderCream bg-white border border-erf-borderCream rounded-md">
          {rows.map((c) => (
            <li key={c.id} className="px-3 py-2 text-sm font-nav" data-testid={`on-hold-contract-${c.id}`}>
              {c.title || c.name || `Contract ${String(c.id).slice(0, 8)}`}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export default function OperationsProjectsTab() {
  const [params] = useSearchParams();
  const status = params.get("status");
  const onHold = status === "on_hold";
  const [rows, setRows] = useState(null);
  useEffect(() => {
    setRows(null);
    api.get("/projects", { params: status ? { status } : {} }).then((r) => setRows(r.data || [])).catch(() => setRows([]));
  }, [status]);
  return (
    <div data-testid="operations-projects-body">
      <div className="mb-4">
        <h2 className="font-display font-semibold text-erf-dark text-lg" data-testid="projects-heading">
          {onHold ? "Projects On Hold" : "Projects"}
        </h2>
        <div className="text-xs font-nav text-erf-dark/60 mt-0.5" data-testid="projects-count">
          {rows === null ? "Loading…" : onHold ? `${rows.length} project(s) with status On Hold` : `${rows.length} project(s) in tenant`}
        </div>
        {status && (
          <Link to="/admin/operations/projects" data-testid="projects-clear-filter"
                className="inline-flex items-center min-h-[44px] text-xs font-nav text-erf-dark underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-erf-gold">
            Show all projects
          </Link>
        )}
      </div>
      {rows !== null && rows.length === 0 && (
        <EmptyState icon={HardHat} title={onHold ? "No projects on hold" : "No projects yet"}
                    subtitle={onHold ? "No project in this workspace has the status On Hold." : "Projects appear here when a contract is signed and converted."} />
      )}
      {rows !== null && rows.length > 0 && (
        <div className="bg-white border border-erf-borderCream rounded-md overflow-x-auto">
          <table className="w-full erf-table">
            <thead className="bg-erf-creamSecondary">
              <tr>
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2">Client</th>
                <th className="text-left px-3 py-2">State</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Completion</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} className="border-t border-erf-borderCream/60" data-testid={`project-row-${p.id}`}>
                  <td className="px-3 py-2 font-nav font-semibold">{p.name || p.title || "—"}</td>
                  <td className="px-3 py-2 mono text-[11px]">{p.client_name || "—"}</td>
                  <td className="px-3 py-2 mono uppercase">{p.state || "—"}</td>
                  <td className="px-3 py-2"><StatusChip status={p.status || "not_configured"} /></td>
                  <td className="px-3 py-2 mono">{p.completion_pct != null ? `${p.completion_pct}%` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {onHold && <OnHoldContracts />}
    </div>
  );
}
